import { useState } from 'react';
import { withNamespaces } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { AvField } from 'availity-reactstrap-validation';

import AdvancedSection from '../advanced-section';
import SectionCard from '../section-card';
import SettingsField from '../settings-field';
import {
    AUTH_MODE_OUT_OPTIONS,
    defaultAuthModeOut,
} from '../../helpers';

const SendingSettings = ({
    t,
    isFetching,
    defaultSettings,
    provider,
    isMicrosoft,
    isCustom,
    secretRequired,
    forceAdvanced,
}) => {
    const [showAdvancedSmtp, setShowAdvancedSmtp] = useState(false);
    const hasSavedSecret = !secretRequired;
    const requiredRule = {
        required: {
            value: !!isCustom,
            errorMessage: t('This field cannot be blank'),
        },
    };

    return (
        <SectionCard
            number={2}
            title={t('Sending settings')}
            description={t('How the mailbox authenticates when the system sends emails.')}
        >
            <Row>
                <Col sm="12" md="6" className="mb-3">
                    <SettingsField
                        id="authModeOut"
                        label={t('Authentication Mode')}
                        required
                        isFetching={isFetching}
                        info={t('How the mailbox authenticates for sending. Gmail and Yahoo use App Password. Microsoft uses Password for SMTP until SMTP OAuth is available.')}
                    >
                        <AvField
                            id="authModeOut"
                            name="authModeOut"
                            type="select"
                            className="form-control"
                            validate={{
                                required: {
                                    value: true,
                                    errorMessage: t('This field cannot be blank'),
                                },
                            }}
                            value={
                                defaultSettings?.authModeOut ||
                                defaultAuthModeOut(provider)
                            }
                        >
                            {AUTH_MODE_OUT_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {t(option.labelKey)}
                                </option>
                            ))}
                        </AvField>
                    </SettingsField>
                </Col>

                <Col sm="12" md="6" className="mb-3">
                    <SettingsField
                        id="smtpSecret"
                        label={t('SMTP password')}
                        required={secretRequired}
                        isFetching={isFetching}
                        info={isMicrosoft
                            ? t('Used to send mail until SMTP OAuth is available. Leave blank on edit to keep the saved password.')
                            : t('For Gmail and Yahoo use an app password, not the normal login password. Leave blank on edit to keep the saved password.')}
                        hint={hasSavedSecret
                            ? t('Password saved. Enter a new password only to replace it.')
                            : (!isMicrosoft && (provider === 'google' || provider === 'yahoo')
                                ? t('Use an app password, not the normal login password.')
                                : undefined)}
                    >
                        <AvField
                            id="smtpSecret"
                            name="smtpSecret"
                            type="password"
                            autoComplete="new-password"
                            placeholder={hasSavedSecret ? '••••••••••••' : ''}
                            className="form-control"
                            validate={{
                                required: {
                                    value: secretRequired,
                                    errorMessage: t('This field cannot be blank'),
                                },
                            }}
                        />
                    </SettingsField>
                </Col>
            </Row>

            <AdvancedSection
                id="advanced-smtp"
                title={t('Advanced SMTP settings')}
                info={t('Hosts are filled from the provider preset. Open Advanced only if IT asked you to override them.')}
                isOpen={showAdvancedSmtp}
                alwaysOpen={forceAdvanced}
                onToggle={() => setShowAdvancedSmtp(!showAdvancedSmtp)}
            >
                <Row>
                    <Col sm="12" md="6" className="mb-3">
                        <SettingsField
                            id="smtpServer"
                            label={t('SMTP Server')}
                            required={isCustom}
                            isFetching={isFetching}
                            info={t('Outgoing mail server used to send emails from this mailbox.')}
                        >
                            <AvField
                                id="smtpServer"
                                name="smtpServer"
                                type="text"
                                className="form-control"
                                validate={requiredRule}
                                value={defaultSettings?.smtpServer || ''}
                            />
                        </SettingsField>
                    </Col>

                    <Col sm="12" md="6" className="mb-3">
                        <SettingsField
                            id="smtpPort"
                            label={t('SMTP Port')}
                            required={isCustom}
                            isFetching={isFetching}
                        >
                            <AvField
                                id="smtpPort"
                                name="smtpPort"
                                type="number"
                                className="form-control"
                                validate={requiredRule}
                                value={defaultSettings?.smtpPort || ''}
                            />
                        </SettingsField>
                    </Col>

                    <Col sm="12" md="6">
                        <SettingsField
                            id="smtpUsername"
                            label={t('SMTP Username')}
                            isFetching={isFetching}
                            info={t('Usually the same as the mailbox email address. Leave blank to use the email address.')}
                        >
                            <AvField
                                id="smtpUsername"
                                name="smtpUsername"
                                type="text"
                                className="form-control"
                                value={defaultSettings?.smtpUsername || ''}
                            />
                        </SettingsField>
                    </Col>
                </Row>
            </AdvancedSection>
        </SectionCard>
    );
};

export default withNamespaces()(SendingSettings);
